/**
 * PhoneInput — reusable phone-number input with country-code picker.
 *
 * Emits an object { country: 'GB', dial: '+44', national: '7700900123', e164: '+447700900123' }
 * so the caller can persist any (or all) of these depending on their schema.
 *
 * Usage:
 *
 *   <PhoneInput
 *     value={form.phone_e164}
 *     country={form.country || 'US'}
 *     onChange={({ e164, country, dial, national }) => setForm({ ...form, phone_e164: e164, country })}
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Search, ChevronDown, Check } from 'lucide-react';
import { COUNTRIES } from '@/lib/countries';

function toE164(dial, national) {
  const digits = String(national || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '';
  return `${dial}${digits}`;
}

// Parse an existing E.164 value into a country + national parts (best-effort)
function splitE164(e164) {
  if (!e164 || typeof e164 !== 'string' || !e164.startsWith('+')) return null;
  // Match longest dial code
  const sorted = [...COUNTRIES].sort((a, b) => (b.dial || '').length - (a.dial || '').length);
  for (const c of sorted) {
    if (e164.startsWith(c.dial)) {
      return { country: c.code, dial: c.dial, national: e164.slice(c.dial.length) };
    }
  }
  return null;
}

const PhoneInput = ({
  value = '',
  country = 'US',
  onChange,
  disabled = false,
  placeholder = 'Phone number',
  className = '',
  required = false,
  autoFocus = false,
  testId = 'phone-input',
}) => {
  // Resolve initial state from either an E.164 `value` or the given `country`
  const initialParsed = useMemo(() => splitE164(value), [value]);
  const initialCountry = initialParsed?.country || country || 'US';
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [national, setNational] = useState(initialParsed?.national || '');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES.find((c) => c.code === 'US'),
    [countryCode]
  );

  // Keep the latest onChange in a ref so the emit-effect can call it
  // without re-firing whenever the parent passes a fresh inline arrow.
  // Without this ref, callers who spread `formData` on every render
  // (e.g. ChurchesManagement) create a new `onChange` reference each
  // render → effect fires → parent setState → new `onChange` → effect
  // fires again → infinite `setState` loop / React error #185.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Emit changes upward — only fires on genuine user input.
  // Guarded with `lastEmittedRef` so re-mounts / prop updates that
  // don't actually change the resolved value don't ping the parent.
  const lastEmittedRef = useRef(null);
  useEffect(() => {
    const dial = selected?.dial || '';
    const e164 = toE164(dial, national);
    if (lastEmittedRef.current === e164 && national === (lastEmittedRef.current || '').replace(dial, '')) {
      return;
    }
    lastEmittedRef.current = e164;
    onChangeRef.current?.({
      country: selected?.code || null,
      dial: selected?.dial || null,
      national: national || '',
      e164,
    });
  }, [countryCode, national, selected]);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        (c.dial || '').includes(query)
    );
  }, [q]);

  return (
    <div className={`flex items-stretch gap-0 border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${className}`} data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex items-center gap-1 px-2.5 border-r bg-slate-50 hover:bg-slate-100 rounded-l-md text-sm disabled:opacity-60"
            data-testid={`${testId}-country-trigger`}
          >
            <span className="text-base leading-none">{selected?.flag || '🌐'}</span>
            <span className="text-slate-700 font-medium">{selected?.dial || '+?'}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search country or dial code…"
                className="h-8 pl-8 text-xs"
                data-testid={`${testId}-country-search`}
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {results.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">No matches</div>
            ) : (
              <ul>
                {results.map((c) => (
                  <li
                    key={c.code}
                    onClick={() => {
                      setCountryCode(c.code);
                      setOpen(false);
                      setQ('');
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${c.code === countryCode ? 'bg-blue-50' : ''}`}
                    data-testid={`${testId}-country-${c.code}`}
                  >
                    <span className="text-base leading-none w-5">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-slate-500 text-xs tabular-nums">{c.dial}</span>
                    {c.code === countryCode && <Check className="w-3.5 h-3.5 text-blue-600" />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <input
        type="tel"
        inputMode="tel"
        value={national}
        onChange={(e) => setNational(e.target.value.replace(/[^\d\s\-().]/g, ''))}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none rounded-r-md min-w-0"
        data-testid={`${testId}-national`}
      />
    </div>
  );
};

export default PhoneInput;
